import { useRef, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { CircleNotch, Lightning, LockSimple } from "@phosphor-icons/react";
import { cn } from "@/lib/utils";
import { groupSlotsByPartOfDay, type PartOfDay } from "@/lib/slot-groups";
import { SarajevoTimeNote } from "./SessionTime";

const PART_LABEL: Record<PartOfDay, string> = {
  morning: "booking.partMorning",
  afternoon: "booking.partAfternoon",
  evening: "booking.partEvening",
};

export function TimeSlots({
  therapistId,
  serviceId,
  dateStr,
  selectedDate,
  selectedTime,
  enabled,
  emergencyDay,
  emergencyPrice,
  onSelectTime,
}: {
  therapistId: string;
  /** Selected service: the grid only offers starts where its full duration fits. */
  serviceId?: string | null;
  dateStr: string;
  selectedDate: Date;
  selectedTime: string | null;
  enabled: boolean;
  /** Date lies inside the regular-lead window and offers emergency slots only. */
  emergencyDay?: boolean;
  emergencyPrice?: number | null;
  onSelectTime: (time: string) => void;
}) {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);

  // Emergency bookings ignore the picked service and run one grid step long
  // (book.ts), so an emergency-only day keeps the service-less grid.
  const slotServiceId = emergencyDay ? null : serviceId ?? null;

  const { data: slotsData, isLoading, isError, refetch } = useQuery({
    // "withBooked" namespaces this from the available-only grid used by
    // RescheduleDialog — same therapistId+date but a different payload shape.
    queryKey: ["available-slots", therapistId, dateStr, "withBooked", slotServiceId],
    queryFn: async () => {
      const params = new URLSearchParams({ date: dateStr, includeBooked: "1" });
      if (slotServiceId) params.set("serviceId", slotServiceId);
      const res = await fetch(`/api/therapists/${therapistId}/available-slots?${params}`);
      // Throw on HTTP error so a failed fetch surfaces as an error state instead
      // of masquerading as "no slots available".
      if (!res.ok) throw new Error("slots");
      return res.json() as Promise<{ slots: Array<{ time: string; endTime: string; booked?: boolean; emergency?: boolean }> }>;
    },
    enabled,
  });

  // On an emergency-only day the endpoint still returns the regular slots the
  // lead time forbids booking; hide them so every visible button is actionable.
  const visibleSlots = emergencyDay
    ? (slotsData?.slots ?? []).filter((s) => s.emergency === true)
    : (slotsData?.slots ?? []);
  const groups = groupSlotsByPartOfDay(visibleSlots);

  useEffect(() => {
    if (ref.current) {
      setTimeout(() => ref.current?.scrollIntoView({ behavior: "smooth", block: "nearest" }), 100);
    }
  }, [selectedDate]);

  if (isLoading) {
    return (
      <div ref={ref} className="flex items-center justify-center py-8">
        <CircleNotch className="size-5 animate-spin text-primary/40" />
      </div>
    );
  }

  if (isError) {
    return (
      <div ref={ref} className="rounded-xl bg-muted/20 py-6 text-center">
        <p className="text-sm text-muted-foreground">{t("booking.slotsLoadError")}</p>
        <button
          type="button"
          onClick={() => void refetch()}
          className="mt-2 text-sm font-medium text-primary underline underline-offset-4"
        >
          {t("booking.retry")}
        </button>
      </div>
    );
  }

  if (groups.length === 0) {
    return (
      <p ref={ref} className="rounded-xl bg-muted/20 py-6 text-center text-sm text-muted-foreground">
        {t("booking.noSlots")}
      </p>
    );
  }

  return (
    <div ref={ref} className="space-y-4">
      {groups[0]?.slots[0] && <SarajevoTimeNote dateStr={dateStr} time={groups[0].slots[0].time} />}
      {emergencyDay && (
        <p className="flex items-center gap-1.5 rounded-xl bg-amber-50 px-3 py-2 text-[13px] font-medium text-amber-900">
          <Lightning className="size-4 shrink-0 text-amber-600" weight="fill" aria-hidden />
          {emergencyPrice != null
            ? t("emergency.slotNote", { price: emergencyPrice })
            : t("emergency.slotNoteNoPrice")}
        </p>
      )}
      {groups.map((group) => (
        <div key={group.part}>
          <p className="mb-2 text-[12px] font-medium uppercase tracking-wide text-muted-foreground/60">
            {t(PART_LABEL[group.part])}
          </p>
          <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
            {group.slots.map((slot) => {
              const active = selectedTime === slot.time;
              const booked = slot.booked === true;
              const urgent = slot.emergency === true && !booked;
              return (
                <button
                  key={slot.time}
                  data-testid={`booking-slot-${slot.time}`}
                  data-emergency={urgent ? "true" : undefined}
                  disabled={booked}
                  aria-label={booked ? `${slot.time}, ${t("booking.slotBooked")}` : slot.time}
                  onClick={() => !booked && onSelectTime(slot.time)}
                  style={booked ? {
                    backgroundImage:
                      "repeating-linear-gradient(135deg, oklch(92% 0.01 84 / 0.6) 0, oklch(92% 0.01 84 / 0.6) 1px, transparent 1px, transparent 7px)",
                  } : undefined}
                  className={cn(
                    "group relative rounded-full border py-2 text-center text-[13.5px] font-medium transition-all",
                    booked
                      ? "cursor-not-allowed border-border/50 text-muted-foreground/55"
                      : active
                        ? urgent
                          ? "border-amber-600 bg-amber-600 text-white"
                          : "border-foreground bg-foreground text-background"
                        : urgent
                          ? "border-amber-400 bg-amber-50 text-amber-900 hover:border-amber-600"
                          : "border-border/70 bg-card text-foreground hover:border-primary",
                  )}
                >
                  {booked ? (
                    <span className="inline-flex items-center justify-center gap-1">
                      <LockSimple className="size-3 shrink-0 opacity-60" weight="fill" aria-hidden />
                      {slot.time}
                    </span>
                  ) : urgent ? (
                    <span className="inline-flex items-center justify-center gap-1">
                      <Lightning className={cn("size-3 shrink-0", active ? "text-white" : "text-amber-600")} weight="fill" aria-hidden />
                      {slot.time}
                    </span>
                  ) : (
                    slot.time
                  )}
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
